import { useEffect, useState } from "react";
import { Gem, Loader2, CheckCircle2, AlertTriangle, History, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";
import { toast } from "sonner";

const API = "/api";
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "");
const POOL_LABEL = { daily: "Warm Up", warmup: "Warm Up", challenge: "Challenge" };
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : "");

function MapBadges({ item }) {
  return (
    <>
      <Badge variant="outline" className="text-[10px]">{POOL_LABEL[item.pool] || cap(item.pool) || "Pool?"}</Badge>
      <Badge variant="outline" className="text-[10px]">{cap(item.world)}</Badge>
      {item.date && <Badge variant="outline" className="text-[10px]">Scheduled {item.date}</Badge>}
    </>
  );
}

function PlanRow({ item }) {
  return (
    <li className="bg-card rounded-lg border border-border/60 px-3 py-2 text-sm flex items-center gap-2 flex-wrap" data-testid={`gem-plan-${item.question_id}`}>
      <span className="font-medium truncate">{item.title}</span>
      <MapBadges item={item} />
      <span className="text-xs text-muted-foreground">
        {item.existing ? `${plural(item.existing, "gem")} already · ` : ""}
        {item.add.map((g) => `+1 near the ${g.near} (${g.x}, ${g.y})`).join(" · ")}
      </span>
    </li>
  );
}

function RunEntry({ run }) {
  return (
    <li className="bg-card rounded-lg border border-border/60 px-3 py-2 text-xs space-y-1" data-testid={`gem-run-${run.log_id}`}>
      <div className="flex items-center gap-2 flex-wrap text-muted-foreground">
        <span className="font-medium text-foreground">{fmtWhen(run.at)}</span>
        {run.actor && <span>by {run.actor}</span>}
        <span>· {plural(run.gems_added, "gem")} added to {plural(run.maps_filled, "map")}{run.skipped?.length ? `, ${plural(run.skipped.length, "map")} skipped` : ""}</span>
      </div>
      {(run.filled || []).slice(0, 8).map((m) => (
        <p key={m.question_id} className="flex items-center gap-1.5"><Gem className="w-3 h-3 text-emerald-500 shrink-0" /> <span className="font-medium">{m.title}</span> <span className="text-muted-foreground">— {m.add.map((g) => `(${g.x}, ${g.y}) near the ${g.near}`).join(", ")}</span></p>
      ))}
      {(run.filled || []).length > 8 && <p className="text-muted-foreground">…and {run.filled.length - 8} more</p>}
    </li>
  );
}

export function GemFillTool({ isMasterAdmin, onFilled }) {
  const [health, setHealth] = useState(null);
  const [running, setRunning] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const load = async () => {
    try {
      const res = await fetch(`${API}/admin/gems/health`, { credentials: "include" });
      if (res.ok) setHealth(await res.json());
    } catch { /* keep loading state */ }
  };
  useEffect(() => { load(); }, []);

  const fill = async () => {
    setConfirm(false);
    setRunning(true);
    try {
      const res = await fetch(`${API}/admin/gems/fill`, { method: "POST", credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || "Filling gems failed"); return; }
      if (!data.gems_added) toast.success("Every Junior map already has 2 hidden gems - nothing to add");
      else toast.success(`Added ${plural(data.gems_added, "hidden gem")} to ${plural(data.maps_filled, "map")}${data.skipped?.length ? ` · ${plural(data.skipped.length, "map")} skipped (no room)` : ""}`);
      await load();
      onFilled?.();
    } catch {
      toast.error("Filling gems failed");
    } finally {
      setRunning(false);
    }
  };

  const plan = health?.plan || [];
  const skipped = health?.skipped || [];
  const runs = health?.runs || [];
  const gemsToAdd = plan.reduce((n, m) => n + m.add.length, 0);

  return (
    <div className="border border-border rounded-xl p-4" data-testid="gem-fill-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <h3 className="font-semibold text-lg mb-1 flex items-center gap-2">
            <Gem className="w-4 h-4 text-emerald-500" />
            Hidden Gems
          </h3>
          <p className="text-sm text-muted-foreground mb-3">
            Tops every Junior Blocks map up to 2 hidden gems: one in the nearest empty cell to the drill and one in the nearest empty cell to the treasure, each at least 2 cells away so the scanner has to travel to find them. Maps that already have 2 gems are left alone; maps with no empty cell are skipped.
          </p>
        </div>
        {isMasterAdmin && (
          <Button onClick={() => setConfirm(true)} disabled={running || !health || plan.length === 0} variant="outline" className="gap-2 border-emerald-400 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/40" data-testid="fill-gems-btn">
            {running ? <><Loader2 className="w-4 h-4 animate-spin" />Adding gems…</> : <><Sparkles className="w-4 h-4" />Fill hidden gems</>}
          </Button>
        )}
      </div>

      {!health ? (
        <p className="text-sm text-muted-foreground flex items-center gap-2" data-testid="gem-health-loading"><Loader2 className="w-4 h-4 animate-spin" /> Checking Junior maps…</p>
      ) : plan.length === 0 && skipped.length === 0 ? (
        <div className="border rounded-lg p-3 text-sm flex items-center gap-2 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-400" data-testid="gem-health-ok">
          <CheckCircle2 className="w-4 h-4" /> All {plural(health.total, "Junior map")} have 2 hidden gems.
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm" data-testid="gem-health-summary">
            <span className="font-medium">{health.full} of {health.total}</span> Junior maps have 2 hidden gems.
          </p>
          {plan.length > 0 && (
            <div className="border rounded-lg p-3 bg-amber-50/60 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800" data-testid="gem-health-plan">
              <p className="text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5 text-amber-700 dark:text-amber-400">
                <Gem className="w-3.5 h-3.5" /> {plural(plan.length, "map")} short of gems · {plural(gemsToAdd, "gem")} to add
              </p>
              <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1">{plan.map((m) => <PlanRow key={m.question_id} item={m} />)}</ul>
            </div>
          )}
          {skipped.length > 0 && (
            <div className="border rounded-lg p-3 bg-red-50/60 dark:bg-red-900/10 border-red-200 dark:border-red-800" data-testid="gem-health-skipped">
              <p className="text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5 text-red-700 dark:text-red-400">
                <AlertTriangle className="w-3.5 h-3.5" /> {plural(skipped.length, "map")} can't be filled
              </p>
              <ul className="space-y-1.5">
                {skipped.map((m) => (
                  <li key={m.question_id} className="bg-card rounded-lg border border-border/60 px-3 py-2 text-sm flex items-center gap-2 flex-wrap" data-testid={`gem-skipped-${m.question_id}`}>
                    <span className="font-medium truncate">{m.title}</span>
                    <MapBadges item={m} />
                    <span className="text-xs text-muted-foreground">{m.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {runs.length > 0 && (
        <div className="mt-4" data-testid="gem-fill-history">
          <p className="text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5 text-muted-foreground"><History className="w-3.5 h-3.5" /> Fill history</p>
          <ul className="space-y-1.5">{runs.map((r) => <RunEntry key={r.log_id} run={r} />)}</ul>
        </div>
      )}

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="fill-gems-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This adds {plural(gemsToAdd, "hidden gem")} to {plural(plan.length, "Junior map")} listed above. Students won't see them until their scanner finds them. You can move or remove any gem later in the map editor.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="fill-gems-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={fill} className="bg-emerald-600 hover:bg-emerald-700" data-testid="fill-gems-confirm-btn">Add gems</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
